import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useClickOutside from '@hooks/useClickOutside';

import { cvrProbabilityTypes, getPercentageValue, probabilityTypes } from '@features/crm/utils/helpers';
import { generateSalesFunnelDashboardParams, toggleCheckbox } from '@features/filter/static/filterUtil';

import { storeCRMProducts } from '@store/actions/crm/CRMCommonAction';
import { storeItemInGlobalFilter } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { findAllSalesFunnelRevenueSummary } from '@services/crmService';

import { convertNumberToUSFormat, getNumberUnit } from '@utils/Util';

const RevenueSummary = ({ enableApiCall }) => {
	const filterFields = useSelector((store) => store.filterFieldStore);
	const [isShowMissedOpportunity, setIsShowMissedOpportunity] = useState(false);
	const crmStore = useSelector((state) => state.crmCommonStore);
	const [salesSummary, setSalesSummary] = useState({});
	const [isShowCvr, setIsShowCvr] = useState(false);
	const opportunityRef = useRef(null);
	const cvrRef = useRef(null);
	const dispatch = useDispatch();

	useEffect(() => {
		if (enableApiCall) {
			(async () => {
				try {
					dispatch(openLoader());
					const params = generateSalesFunnelDashboardParams(filterFields, false);
					const data = await findAllSalesFunnelRevenueSummary(params);
					setSalesSummary(data);
					dispatch(closeLoader());
				} catch {
					dispatch(closeLoader());
				}
			})();
		}
	}, [filterFields, enableApiCall]);

	useClickOutside(opportunityRef, () => setIsShowMissedOpportunity(false));

	useClickOutside(cvrRef, () => setIsShowCvr(false));

	const renderMissedOpportunityInfo = () => {
		if (isShowMissedOpportunity) {
			return (
				<div className="info-details missed-opportunity">
					<ul className="with-bullet">
						<li className="d-flex justify-content-between regular-12 gray_dark_01">
							<span>Request</span>
							<span className="red">
								${getNumberUnit(salesSummary?.missedOpportunity?.request) || 0}
							</span>
						</li>
						<li className="d-flex justify-content-between regular-12 gray_dark_01">
							<span>Partially Approved</span>
							<span className="red">
								${getNumberUnit(salesSummary?.missedOpportunity?.partiallyApproved) || 0}
							</span>
						</li>
						<li className="d-flex justify-content-between regular-12 gray_dark_01">
							<span>Approved</span>
							<span className="red">$0</span>
						</li>
						<li className="d-flex justify-content-between regular-12 gray_dark_01">
							<span>PO Received</span>
							<span className="red">
								${getNumberUnit(salesSummary?.missedOpportunity?.poReceived) || 0}
							</span>
						</li>
					</ul>
				</div>
			);
		}
	};

	const renderCVRInfo = () => {
		if (!isShowCvr) return null;

		return (
			<div className="info-details cvr">
				<ul className="with-bullet">
					{cvrProbabilityTypes.map(({ type, currentLabel, nextLabel, fieldName }) => {
						return (
							<li key={type} className="d-flex justify-content-between regular-12 gray_dark_01">
								<span>{`${currentLabel} to ${nextLabel}`}</span>
								<span>{salesSummary?.conversionRateResponse?.[fieldName] || 0}%</span>
							</li>
						);
					})}
				</ul>
			</div>
		);
	};

	const getProbabilityInfo = (probabilityType) => {
		return salesSummary?.probabilityCardResponseList?.find((item) => item.constant === probabilityType);
	};

	const isShowRejected = (type) => {
		let probabilityList = filterFields?.probabilityList?.map((item) => item.key);
		if (probabilityList === undefined || probabilityList?.length === 0) {
			return type !== 'REJECTED';
		} else {
			return probabilityList.includes(type);
		}
	};

	const renderProbabilityInfo = () => {
		const handleClickProbability = (probabilityInfo) => {
			let data = {
				name: 'probabilityList',
				key: probabilityInfo.constant,
				value: probabilityInfo.name,
				identifier: `probabilityList_${probabilityInfo.constant}`,
				checked: !toggleCheckbox(
					filterFields,
					'probabilityList',
					`probabilityList_${probabilityInfo.constant}`,
					probabilityInfo.constant
				)
			};
			dispatch(storeItemInGlobalFilter(data));
			dispatch(storeCRMProducts([]));
		};

		return (
			<div className="preorder-info-tab">
				{probabilityTypes.map((probabilityType) => {
					const probabilityInfo = getProbabilityInfo(probabilityType.type);
					return (
						<div
							key={probabilityType.type}
							className={`single-tab-item cursor-pointer ${
								probabilityInfo === undefined ? 'disabled' : ''
							} ${isShowRejected(probabilityType.type) ? 'active' : ''}`}
							onClick={() => {
								if (probabilityInfo !== undefined) {
									handleClickProbability(probabilityInfo);
								}
							}}
						>
							{probabilityType.type === 'REJECTED' && (
								<p className="regular-12 weight-600 red mb-1">Click to see details</p>
							)}

							<h3 className="semibold-12 mb-12">
								{probabilityType.label} ({probabilityType.percentage}%)
							</h3>
							<p className="regular-12 weight-300 mb-1">Brand / Style / Units</p>
							<p className="semibold-12">
								{getNumberUnit(probabilityInfo?.noOfBrand) || 0} /{' '}
								{getNumberUnit(probabilityInfo?.noOfStyle) || 0} styles /{' '}
								{getNumberUnit(probabilityInfo?.noOfUnit) || 0} units
							</p>
							<div className="d-flex justify-content-between mt-12">
								<div>
									<p className="regular-12 weight-300 mb-1">Total Value</p>
									<p className="semibold-12">
										${getNumberUnit(probabilityInfo?.totalValue) || 0}
									</p>
								</div>
								<div>
									<p className="regular-12 weight-300 mb-1">Potential Value</p>
									<p className="semibold-12">
										$
										{probabilityInfo?.totalValue
											? getNumberUnit(
													(probabilityInfo?.totalValue *
														probabilityType.percentage) /
														100
												)
											: 0}
									</p>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	const getNumberOfDesigns = (isNitexDesign) => {
		let styleInfo = salesSummary?.designedByList?.find((item) => item.isNitexDesign === isNitexDesign);
		return styleInfo?.count || 0;
	};

	const renderSalesAnalytics = () => {
		let totalMissedOpportunity = 0;
		for (let item in salesSummary?.missedOpportunity) {
			totalMissedOpportunity += salesSummary.missedOpportunity[item];
		}

		let totalRevenue = 0;
		let totalPcs = 0;
		let avgOrderValue = 0;
		let totalAchived = 0;
		probabilityTypes.forEach((probabilityType) => {
			const probabilityInfo = getProbabilityInfo(probabilityType.type);
			if (probabilityInfo?.constant === 'PO_RECEIVED') {
				totalAchived = probabilityInfo?.totalValue;
				avgOrderValue = probabilityInfo?.totalValue / probabilityInfo?.noOfStyle;
			}
			if (
				probabilityInfo !== undefined &&
				!['ORDER_PLACED', 'PO_RECEIVED'].includes(probabilityInfo?.constant)
			) {
				totalRevenue += ((probabilityInfo?.totalValue || 0) * probabilityType.percentage) / 100;
				totalPcs += ((probabilityInfo?.noOfUnit || 0) * probabilityType.percentage) / 100;
			}
		});

		return (
			<div className="preorder-summary">
				<div className="single-item">
					<p className="regular-12 weight-300 mb-1">Achieved</p>
					<h3 className="semibold-12 mb-0">${getNumberUnit(totalAchived)}</h3>
				</div>
				<div className="single-item">
					<p className="regular-12 weight-300 mb-1">Revenue Pipeline</p>
					<h3 className="semibold-12 mb-0">
						${getNumberUnit(totalRevenue)} / {getNumberUnit(totalPcs)} Units
					</h3>
				</div>
				<div className="single-item" ref={opportunityRef}>
					<p className="regular-12 weight-300 mb-1">
						Missed Opportunity{' '}
						<img
							className="cursor-pointer"
							src={`${process.env.PUBLIC_URL}/icons/info.svg`}
							alt="info"
							onClick={() => setIsShowMissedOpportunity((prev) => !prev)}
						/>
					</p>
					<h3 className="semibold-12 mb-0">${getNumberUnit(totalMissedOpportunity)}</h3>
					{renderMissedOpportunityInfo()}
				</div>
				<div className="single-item" ref={cvrRef}>
					<p className="regular-12 weight-300 mb-1">
						CVR{' '}
						<img
							className="cursor-pointer"
							src={`${process.env.PUBLIC_URL}/icons/info.svg`}
							alt="info"
							onClick={() => setIsShowCvr((prev) => !prev)}
						/>
					</p>
					<h3 className="semibold-12 mb-0">
						{salesSummary?.conversionRateResponse?.avgConversionRate}%
					</h3>
					{renderCVRInfo()}
				</div>
				<div className="single-item">
					<p className="regular-12 weight-300 mb-1">Conversion Time</p>
					<h3 className="semibold-12 mb-0">
						{convertNumberToUSFormat(salesSummary?.conversionTime, true) || 0}WD
					</h3>
				</div>
				<div className="single-item">
					<p className="regular-12 weight-300 mb-1">Avg Order Value (Per Style)</p>
					<h3 className="semibold-12 mb-0">${getNumberUnit(avgOrderValue) || 0}</h3>
				</div>
				<div className="single-item">
					<div className="orders-summary mt-1 mb-0">
						<div className="single-summary p-0 d-flex align-items-center">
							<img src={`${process.env.PUBLIC_URL}/icons/chart.svg`} alt="chart" />
							<div className="ml-2">
								<p className="regular-12 gray_dark_02">
									<span className="c-indicator nitex" />
									Nitex design{' '}
									<span className="semibold-12 ml-1">
										{getNumberUnit(getNumberOfDesigns(true))} (
										{getPercentageValue(
											getNumberOfDesigns(true),
											getNumberOfDesigns(false)
										)}
										%)
									</span>
								</p>
								<p className="regular-12 gray_dark_02">
									<span className="c-indicator buyer" />
									Buyer design{' '}
									<span className="semibold-12 ml-1">
										{getNumberUnit(getNumberOfDesigns(false))} (
										{getPercentageValue(
											getNumberOfDesigns(false),
											getNumberOfDesigns(true)
										)}
										%)
									</span>
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<>
			{crmStore?.isShowSalesFunnelSummary && renderProbabilityInfo()}
			{crmStore?.isShowSalesFunnelSummary && renderSalesAnalytics()}
		</>
	);
};

export default RevenueSummary;
